import React, { useState } from 'react';
import { useGMB } from '../contexts/GMBContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toolsAPI, repliesAPI } from '../lib/api';
import { 
  MessageSquare, Copy, Check, Save, ThumbsUp, ThumbsDown, Minus,
  Star, User, Clock, Sparkles, Pencil, QrCode, Link2, ExternalLink, Download, X
} from 'lucide-react';
import { toast } from 'sonner';

const ReviewReplyPage = () => {
  const { language } = useLanguage();
  const { gmbProfile, isConnected } = useGMB();
  
  const [formData, setFormData] = useState({
    review_text: '',
    sentiment: 'Positive',
    business_type: gmbProfile?.category || '',
    language: language === 'hi' ? 'Hindi' : 'English'
  });
  
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [replyMode, setReplyMode] = useState('ai'); // 'ai' or 'manual'
  const [manualReply, setManualReply] = useState('');
  const [showGetReviews, setShowGetReviews] = useState(false);
  const [reviewLinkCopied, setReviewLinkCopied] = useState(false);

  // Mock reviews from the business
  const mockReviews = [
    {
      id: 'r1',
      author: 'Priya Sharma',
      rating: 5,
      text: 'Amazing coffee and great ambiance! The staff was very friendly and the cold brew is to die for. Will definitely come back!',
      date: '2 days ago',
      sentiment: 'Positive'
    },
    {
      id: 'r2',
      author: 'Rahul M.',
      rating: 3,
      text: 'Coffee was okay but the service was a bit slow. Had to wait 20 minutes for my order during non-peak hours.',
      date: '1 week ago',
      sentiment: 'Neutral'
    },
    {
      id: 'r3',
      author: 'Ankit Patel',
      rating: 2,
      text: 'Disappointed with my visit. The pastries were stale and the wifi wasn\'t working. Expected better for the price.',
      date: '2 weeks ago',
      sentiment: 'Negative'
    }
  ];

  const sentiments = [
    { value: 'Positive', icon: <ThumbsUp className="w-5 h-5" />, color: 'bg-[#E6F4EA] text-[#1E8E3E]' },
    { value: 'Neutral', icon: <Minus className="w-5 h-5" />, color: 'bg-[#FEF7E0] text-[#F9AB00]' },
    { value: 'Negative', icon: <ThumbsDown className="w-5 h-5" />, color: 'bg-[#FCE8E6] text-[#D93025]' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setReplies([]);
    
    try {
      const response = await toolsAPI.generateReplies({
        ...formData,
        business_type: formData.business_type || gmbProfile?.category
      });
      setReplies(response.data.replies || []);
      toast.success('Replies generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate replies');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copied!');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleSaveReply = async (reply) => {
    try {
      await repliesAPI.save({
        review_text: formData.review_text,
        generated_reply: reply.reply || reply,
        language: formData.language
      });
      toast.success('Reply saved!');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleSaveManualReply = async () => {
    if (!manualReply.trim()) { toast.error('Please write a reply'); return; }
    if (!formData.review_text.trim()) { toast.error('Please enter the review text first'); return; }
    try {
      await repliesAPI.save({
        review_text: formData.review_text,
        generated_reply: manualReply,
        language: formData.language
      });
      toast.success('Reply saved!');
      setManualReply('');
    } catch { toast.error('Failed to save reply'); }
  };

  // Generate Google review link from Place ID or business name
  const getReviewLink = () => {
    const placeId = gmbProfile?.placeId || '';
    if (placeId) {
      return `https://search.google.com/local/writereview?placeid=${placeId}`;
    }
    const name = gmbProfile?.name || '';
    if (name) {
      return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(name)}`;
    }
    return '';
  };

  const handleCopyReviewLink = () => {
    const link = getReviewLink();
    if (link) {
      navigator.clipboard.writeText(link);
      setReviewLinkCopied(true);
      toast.success('Review link copied!');
      setTimeout(() => setReviewLinkCopied(false), 2000);
    } else {
      toast.error('Connect your Google Business Profile to generate review link');
    }
  };

  const generateQRCodeUrl = () => {
    const link = getReviewLink();
    return `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(link)}`;
  };

  const handleSelectReview = (review) => {
    setFormData({
      ...formData,
      review_text: review.text,
      sentiment: review.sentiment
    });
    setReplies([]);
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-[#1E8E3E]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Review Reply Generator
            </h1>
            <p className="text-[#5F6368]">Generate professional responses to customer reviews</p>
          </div>
        </div>
        <button
          onClick={() => setShowGetReviews(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#1A73E8] text-white rounded-lg text-sm font-medium hover:bg-[#1557B0] transition-colors"
          data-testid="get-more-reviews-btn"
        >
          <QrCode className="w-4 h-4" />
          Get More Reviews
        </button>
      </div>

      {/* Get More Reviews Modal */}
      {showGetReviews && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGetReviews(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="get-reviews-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#202124] flex items-center gap-2">
                <QrCode className="w-5 h-5 text-[#1A73E8]" />
                Get More Reviews
              </h3>
              <button onClick={() => setShowGetReviews(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#5F6368] mb-5">Share this link or QR code with customers to make it easy for them to leave reviews.</p>
            
            <div className="mb-5">
              <label className="block text-sm font-medium text-[#202124] mb-2">Review Link</label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={getReviewLink() || 'Connect Google Business Profile to generate link'}
                  className="flex-1 input-google text-sm bg-[#F8F9FA]"
                  data-testid="review-link-input"
                />
                <button onClick={handleCopyReviewLink} className="px-3 py-2 border rounded-lg hover:bg-gray-50" data-testid="copy-review-link-btn">
                  {reviewLinkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
                {getReviewLink() && (
                  <a href={getReviewLink()} target="_blank" rel="noreferrer" className="px-3 py-2 border rounded-lg hover:bg-gray-50">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>

            {getReviewLink() && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#202124] mb-2">QR Code</label>
                <div className="bg-[#F8F9FA] border rounded-xl p-6 flex flex-col items-center">
                  <img src={generateQRCodeUrl()} alt="Review QR Code" className="w-48 h-48" data-testid="review-qr-code" />
                  <p className="text-xs text-[#9AA0A6] mt-3">Customers can scan this QR code to leave a review</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              {getReviewLink() && (
                <a
                  href={generateQRCodeUrl()}
                  download="review-qr-code.png"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4" /> Download QR
                </a>
              )}
              <button
                onClick={handleCopyReviewLink}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50"
                data-testid="copy-link-btn"
              >
                <Link2 className="w-4 h-4" /> Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Reviews Panel */}
        {isConnected && (
          <div className="dashboard-card lg:col-span-1">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Recent Reviews
            </h3>
            <div className="space-y-3">
              {mockReviews.map((review) => (
                <button
                  key={review.id}
                  onClick={() => handleSelectReview(review)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    formData.review_text === review.text 
                      ? 'border-[#1A73E8] bg-[#E8F0FE]' 
                      : 'border-[#DADCE0] hover:border-[#5F6368]'
                  }`}
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-[#F1F3F4] rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-[#5F6368]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-[#202124]">{review.author}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-3 h-3 ${i < review.rating ? 'fill-[#F9AB00] text-[#F9AB00]' : 'text-[#DADCE0]'}`} 
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#5F6368] line-clamp-2">{review.text}</p>
                  <p className="text-xs text-[#9AA0A6] mt-1">{review.date}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <div className={`dashboard-card ${isConnected ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
          {/* Mode Toggle */}
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => setReplyMode('ai')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${replyMode === 'ai' ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'}`}
              data-testid="mode-ai"
            >
              <Sparkles className="w-4 h-4" /> AI Generate
            </button>
            <button
              onClick={() => setReplyMode('manual')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${replyMode === 'manual' ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'}`}
              data-testid="mode-manual"
            >
              <Pencil className="w-4 h-4" /> Write Manually
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Customer Review
              </label>
              <textarea
                value={formData.review_text}
                onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                className="input-google min-h-[120px]"
                placeholder="Paste or select a customer review..."
                required
                data-testid="review-text-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-3">
                Review Sentiment
              </label>
              <div className="flex gap-2">
                {sentiments.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, sentiment: s.value })}
                    className={`flex-1 p-3 rounded-lg border-2 font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                      formData.sentiment === s.value
                        ? `border-current ${s.color}`
                        : 'border-[#DADCE0] text-[#5F6368] hover:border-[#5F6368]'
                    }`}
                    data-testid={`sentiment-${s.value.toLowerCase()}`}
                  >
                    {s.icon}
                    {s.value}
                  </button>
                ))}
              </div>
            </div>

            {replyMode === 'manual' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#202124] mb-2">Your Reply</label>
                  <textarea
                    value={manualReply}
                    onChange={(e) => setManualReply(e.target.value)}
                    className="input-google min-h-[120px]"
                    placeholder="Write your reply to this review..."
                    data-testid="manual-reply-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSaveManualReply}
                  className="btn-google w-full flex items-center justify-center gap-2"
                  data-testid="save-manual-reply-btn"
                >
                  <Save className="w-5 h-5" /> Save Reply
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#202124] mb-2">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="input-google"
                    data-testid="language-select"
                  >
                    <option value="English">English</option>
                    <option value="Hindi">Hindi</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-google w-full flex items-center justify-center gap-2"
                  data-testid="generate-replies-btn"
                >
                  {loading ? (
                    <><Sparkles className="w-5 h-5 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-5 h-5" /> Generate Replies</>
                  )}
                </button>
              </>
            )}
          </form>
        </div>

        {/* Results */}
        <div className="dashboard-card lg:col-span-1">
          <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            Generated Replies
          </h3>
          
          {replies.length === 0 && !loading && (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 mx-auto text-[#DADCE0]" />
              <p className="mt-4 text-[#5F6368]">Your replies will appear here</p>
            </div>
          )}
          
          {loading && (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto text-[#1A73E8] animate-pulse" />
              <p className="mt-4 text-[#5F6368]">Creating replies...</p>
            </div>
          )}
          
          <div className="space-y-4">
            {replies.map((reply, index) => (
              <div key={index} className="p-4 bg-[#F8F9FA] rounded-lg">
                {reply.tone && (
                  <span className="inline-block text-xs font-medium text-[#5F6368] mb-2 uppercase">
                    {reply.tone} Response
                  </span>
                )}
                <p className="text-[#202124] text-sm whitespace-pre-wrap">
                  {reply.reply || reply}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleCopy(reply.reply || reply, index)}
                    className="btn-outline text-xs py-1.5 px-3 flex items-center gap-1"
                    data-testid={`copy-reply-${index}`}
                  >
                    {copiedIndex === index ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedIndex === index ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => handleSaveReply(reply)}
                    className="btn-google text-xs py-1.5 px-3 flex items-center gap-1"
                    data-testid={`save-reply-${index}`}
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewReplyPage;
